import React, { createContext, useState, useContext, useEffect } from 'react';
import translations from '../translations';
import logger from './../utils/logger.js';

// Create the context
const LanguageContext = createContext();

// Provider component 
export const LanguageProvider = ({ children }) => {
  // Initialize from localStorage if available
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'en'; // Default to English
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
    logger.log(`Language set to: ${language}`);
  }, [language]);

  // Toggle function
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'nl' ? 'en' : 'nl');
  };

  // Translation function
  const t = (key) => {
    if (!key) return '';
    
    // List of keys that don't need translation (they should remain as-is)
    const noTranslationNeeded = [
      'Correspondentieadres', 'Correspondentieadres Straat', 'Correspondentieadres Huisnummer',
      'Correspondentieadres Toevoeging', 'Correspondentieadres Postcode', 'Correspondentieadres Plaats',
      'Telefoonnummer', 'Telefoonnummer Mobiel', 'URL', 'Company.Info Link', 'LinkedIn Link',
      'Kamer van Koophandel nummer', 'Vestigingsnummer', 'Rechtsvorm Omschrijving', 
      'Klasse Aantal Medewerkers Locatie_Omschrijving', 'SBI-code locatie', 'SBI-code locatie_Omschrijving',
      'SBI-code 2-cijferig Locatie', 'SBI-code 2-cijferig Locatie_Omschrijving', 'Mailstop',
      'SizoKey Ultimate Parent', 'Instellingsnaam Ultimate Parent', 
      'Klasse Aantal Medewerkers Concern_Omschrijving', 'Klasse Aantal Locaties Van Concern_Omschrijving',
      'Company.info_IT_gedragstype', 'DMU_Locatie', 'DMU_Concern', 
      'CP Functie Code Verantwoordelijk Bedrijfsvoering_Omschrijving', 'CP Titel Verantwoordelijk Bedrijfsvoering',
      'CP Voorletters Verantwoordelijk Bedrijfsvoering', 'CP Tussenvoegsel Verantwoordelijk Bedrijfsvoering',
      'CP Achternaam Verantwoordelijk Bedrijfsvoering', 'claimed_by', 'locationsNotSpecified',
      // Common field names that should not be translated
      'records', 'Instellingsnaam', 'Vestigingsadres', 'Domain', 'Website'
    ];

    // Critical translations that should always be checked first
    const criticalKeys = ['signIn', 'signInWithMicrosoft', 'usernameOrEmail', 'password', 'or'];
    
    if (criticalKeys.includes(key)) {
      const translatedValue = translations[language][key];
      if (!translatedValue) {
        logger.error(`Missing critical translation for key: ${key} in language: ${language}`);
        return key;
      }
      return translatedValue;
    }

    // Always handle these specific keys directly from translations without special checking
    const alwaysTranslate = [
      'appTitle', 'appSubtitle', 'disclaimer', 'pleaseReadAndAccept', 
      'iAcknowledgeAndAccept', 'accept', 'disclaimerRequired', 'scrollToBottom'
    ];
    if (alwaysTranslate.includes(key)) {
      return translations[language][key] || key;
    }

    // If key is in the no-translation list, return it as-is without warning
    if (noTranslationNeeded.includes(key)) {
      return key;
    }

    // First check if translation exists - if it does, use it regardless of naming pattern
    if (translations[language][key]) {
      return translations[language][key];
    }

    // Only if translation doesn't exist, check if the key looks like a field name or business term
    // This checks for keys that contain:
    // - Capital letters in the middle (camelCase or PascalCase)
    // - Special characters like underscore, dot, or dash
    // - Has spaces and starts with a capital letter
    if (
      /[A-Z]/.test(key.substring(1)) || // Has capital letters after first position
      /[._-]/.test(key) ||              // Contains dots, underscores, or dashes
      (key.includes(' ') && /^[A-Z]/.test(key)) // Contains spaces and starts with capital
    ) {
      return key; // Return field names as-is without warnings
    }

    // If translation is missing and it's not a field name, log warning
    logger.warn(`Missing translation: ${key} for language: ${language}`);
    return key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isNL: language === 'nl',
    isEN: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for easy usage
export const useLanguage = () => useContext(LanguageContext);
